import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { PrezzoConvenzionato } from "@/components/accordi/PrezzoConvenzionato";
import { VerificaDiritto } from "@/components/accordi/VerificaDiritto";
import { ConsulenzaDialog } from "@/components/accordi/ConsulenzaDialog";
import { useNoIndex } from "@/components/accordi/accordiUtils";
import { Button } from "@/components/ui/button";
import { Clock, Loader2, MessageCircle, ShieldCheck, XCircle } from "lucide-react";

const STATO_BOX = {
  in_verifica: [Clock, "bg-sky-50 text-sky-900", "Documentazione ricevuta: un operatore sta verificando il tuo diritto alle condizioni dell'accordo."],
  presa_in_carico: [Clock, "bg-sky-50 text-sky-900", "La verifica è stata presa in carico da un operatore."],
  approvato: [ShieldCheck, "bg-emerald-50 text-emerald-900", "Diritto verificato: un consulente ti contatterà con la proposta."],
  rifiutato: [XCircle, "bg-red-50 text-red-900", "Non è stato possibile confermare il diritto alle condizioni dell'accordo."],
};

// Preventivo salvato: riepilogo, ripresa e verifica del diritto all'agevolazione.
export default function AccordoPreventivo() {
  const { token } = useParams();
  const [p, setP] = useState(undefined);
  const [cons, setCons] = useState(false);
  useNoIndex();
  const load = () => api.get(`/public/accordi/preventivi/${token}`).then(({ data }) => setP(data)).catch(() => setP(null));
  useEffect(() => { load(); }, [token]); // eslint-disable-line

  if (p === undefined) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;
  if (p === null) return <div className="flex min-h-screen items-center justify-center text-muted-foreground" data-testid="preventivo-non-trovato">Preventivo non trovato.</div>;
  const box = STATO_BOX[p.stato];
  const puoProcedere = ["salvato", "integrazione"].includes(p.stato) && !p.consulenza_obbligatoria && p.accordo_attivo;

  return (
    <div className="min-h-screen bg-bone" data-testid="accordo-preventivo">
      <header className="border-b bg-white">
        <div className="mx-auto flex max-w-5xl items-center gap-4 px-5 py-4">
          <img src="/assets/assietica-logo.png" alt="Assietica" className="h-9" />
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-terracotta">Accordo di Maggior Favore</span>
        </div>
      </header>
      <div className="mx-auto grid max-w-5xl gap-8 px-5 py-10 lg:grid-cols-5">
        <div className="space-y-4 lg:col-span-2">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-terracotta">Accordo {p.codice}</p>
          <h1 className="font-heading text-3xl font-semibold text-forest">{p.product_label}</h1>
          <p className="text-sm text-muted-foreground">{p.accordo_nome}{p.soluzione ? ` · ${p.soluzione}` : ""}</p>
          <PrezzoConvenzionato r={p} testid="preventivo-prezzo" />
          <p className="text-xs text-muted-foreground">Intestato a {p.ente?.ragione_sociale ? `${p.ente.ragione_sociale} (${p.ente.tipo_ente}) · referente ` : ""}{p.cliente?.nome} {p.cliente?.cognome}. Stato: <b data-testid="preventivo-stato">{p.stato_label}</b></p>
          {p.indicativo && <p className="text-xs text-muted-foreground" data-testid="preventivo-indicativo">{p.direzione ? "Premio indicativo: la Direzione Tecnica lo conferma dopo l'esame di bilanci e statuto." : "Premio indicativo: l'Agenzia lo conferma dopo il questionario assuntivo."}</p>}
          <Button variant="outline" onClick={() => setCons(true)} data-testid="preventivo-consulenza" className="w-full gap-1.5"><MessageCircle className="h-4 w-4" />Richiedi una consulenza dedicata</Button>
        </div>
        <div className="rounded-2xl border bg-white p-6 lg:col-span-3">
          {box && <div className={`mb-4 flex gap-2 rounded-xl p-4 text-sm ${box[1]}`} data-testid="preventivo-stato-box">{(() => { const I = box[0]; return <I className="h-5 w-5 shrink-0" />; })()}{box[2]}{p.note_operatore && p.stato === "rifiutato" ? ` ${p.note_operatore}` : ""}</div>}
          {p.stato === "integrazione" && <div className="mb-4 rounded-xl bg-amber-50 p-4 text-sm text-amber-900" data-testid="preventivo-integrazione">Integrazione richiesta: {p.note_operatore}</div>}
          {p.consulenza_obbligatoria && <p className="text-sm text-amber-900" data-testid="preventivo-solo-consulenza">Per questo prodotto è prevista una consulenza: richiedila e ti ricontattiamo.</p>}
          {puoProcedere && <>
            <h2 className="mb-1 font-heading text-xl font-semibold text-forest">Procedi con la proposta</h2>
            <p className="mb-5 text-sm text-muted-foreground">Prima della proposta verifichiamo che tu abbia diritto alle condizioni dell'accordo. Nessuna proposta viene emessa senza esito positivo.</p>
            <VerificaDiritto p={p} onInviata={load} />
          </>}
          {!p.accordo_attivo && ["salvato", "integrazione"].includes(p.stato) && <p className="text-sm text-red-800">L'accordo non è più attivo.</p>}
        </div>
      </div>
      <ConsulenzaDialog open={cons} onOpenChange={setCons} codice={p.codice} product={p.product} productLabel={p.product_label} />
    </div>
  );
}
